<script setup lang="ts">
import type { CyclabilityCitiesData } from '@/composables/cyclability/cities/cyclabilityCitiesData'

defineOptions({
  name: 'CityCyclabilitySharedBikesPerfMetric',
})

const props = defineProps<{
  showAllData?: boolean
}>()

const { t } = useI18n()
const { writeCache } = useWriteCacheForMetric('cityCyclabilitySharedBikesPerf')

const cityData = inject('cityData') as ReturnType<typeof useCyclabilityCitiesData>
const { dataInRange, data: allData, cityDateRange, cityDateRangeLimit, city } = cityData

const usedDateRange = computed(() => props.showAllData ? cityDateRange.value : cityDateRangeLimit.value)

function updateCachedData(data: CyclabilityCitiesData[]) {
  if (!city.value) {
    return
  }

  const ffSeries: [string, number][] = []
  const sbSeries: [string, number][] = []
  const series: { name: string, label: string, color: string, data: [string, number][] }[] = []

  if (data?.length > 0) {
    for (const [index, item] of data.entries()) {
      ffSeries[index] = [formatDate(item.date), item.freeFloatingTrips]
      sbSeries[index] = [formatDate(item.date), item.stationBasedTrips]
    }

    if (sbSeries.filter(item => item[1] !== undefined).length > 0) {
      series.push({
        name: 'station',
        label: 'Station-based bikes',
        color: '#bbc9f5',
        data: sbSeries,
      })
    }

    if (ffSeries.filter(item => item[1] !== undefined).length > 0) {
      series.push({
        name: 'free-floating',
        label: 'Free-floating bikes',
        color: '#5578e6',
        data: ffSeries,
      })
    }

    writeCache({
      city: city.value,
      dateRange: usedDateRange.value,
    }, {
      series,
    })
  } else {
    writeCache({
      city: city.value,
      dateRange: usedDateRange.value,
    }, {
      series: [{
        name: 'free-floating',
        label: 'Free-floating bikes',
        color: '#5578e6',
        data: [],
      }],
    })
  }
}

watch([dataInRange, allData], ([dataInRange, allData]) => {
  if (!allData) {
    return
  }

  if (props.showAllData) {
    updateCachedData(allData)
  } else {
    updateCachedData(dataInRange)
  }
}, { immediate: true })

function getColor(d: { name: string }) {
  return PALETTE_COLORS_WEEK[d.name]
}
</script>

<template>
  <MetricCard
    kpi="cityCyclabilitySharedBikesPerf"
    :used-filters="['dateRange']"
    :variables="{
      dateRange: usedDateRange,
    }"
  >
    <template #title>
      {{ t('cyclability.shared_bike_perf.title') }}
    </template>

    <template #default="{ data }">
      <BarMetric
        :data="data"
        :options="{ color: getColor }"
      />
    </template>
  </MetricCard>
</template>
